// #25 automatic payments, phase 6: the pure logic behind changing automatic payments. Two workflows do it, both started
// for one signatory:
// - Change automatic payments amounts (from My Account, or from a Limit reached, Change target or Raise operating target
//   card, as that card's child): new amounts, signed on the public ledger as a new maximum allowance (the operating
//   account makes and pays the schedule, nodeStream pays every signature); they apply once it runs;
// - Change the automatic payments card (from My Account; a Payment failed card takes the new card on itself): the new card
//   is held, never live, while the account signatories sign one public ledger record saying who changed it (round 15: a
//   scheduled transfer of 1 tinybar from the company account to its operating account, its memo naming who changed the
//   card; no card details on the ledger); it goes live once the record has run on the ledger.
// The server decides (ns-lambda-ts public-ledger/autopay/{start-change,change-intent,confirm-change-card,propose-change,
// card-switch}.ts); this only chooses what the cards show and offer.
import { formatCredits } from './public-ledger-credits.js';
import { formatPence, formatUnitPence } from './credit-pricing.js';
import {
  creditsForPence,
  penceForCredits,
  type AutoAmounts,
  type AutoPaymentRow,
  type AmountField,
  type ProposedSettings,
  type SettingsDraft,
} from './auto-payments.js';

// --- Copy approved by the product owner on 2026-10-28 (G14, recheck follow-ups); use exactly ---

// The change-amounts signing card, above the Now / After table
export const CHANGE_AMOUNTS_SIGN_COPY =
  "Your signature changes the company's automatic payments to the settings below. nodeStream pays the fee for your " +
  "signature; the company's operating account paid to set the change up.";

export const CHANGE_AMOUNTS_INTRO =
  "Change when and how much the company's automatic payments top up. The signatories sign the new settings on the " +
  'public ledger; they apply once signed.';

export const CHANGE_CARD_INTRO =
  "Enter the new automatic payment card. The signatories sign a public ledger record saying you changed it; the old card is removed once it's signed.";

// What a Change button says, with an Open link to its card, when a change of that kind already waits for signatures
// (start-change, and card-action for a card's Raise or Change, answer waiting instead of starting one)
export const CHANGE_WAITING_TEXT = {
  AMOUNTS: 'A change of amounts is already waiting for signatures.',
  CARD: 'A change of card is already waiting for signatures.',
} as const;

// A change card's last line once its change has ended
export const CHANGE_DONE_TEXT = {
  AMOUNTS: 'The new settings are signed and in use.',
  CARD: 'The new card is signed and in use.',
  CANCELLED: 'This change was cancelled.',
} as const;

// A closed change card's line while its change waits for the signatories (round 3 extras); also the Change amounts card's
// line between proposing and the job closing
export const CHANGE_SIGNING_TEXT = {
  AMOUNTS: 'The new amounts are waiting for the signatories to sign them.',
  CARD: 'The new card is waiting for the signatories to sign it.',
} as const;

// What a button that starts a change (My Account's, and the Limit reached, Change target and Raise operating target
// cards') says while it starts and when it can't
export const CHANGE_STARTING = 'Starting the change…';
export const CHANGE_START_FAILED = 'The change could not be started. Please try again.';

// The Change amounts card's button: proposes the new amounts, or with nothing changed closes the job on a second press
export const CHANGE_AMOUNTS_BUTTON = 'Complete';

// The Change amounts card, above its button, before anything is proposed
export const CHANGE_AMOUNTS_BEFORE =
  'After proposing new amounts, every signatory is asked to sign them on the public ledger, and they apply once signed.';

// The Change amounts card's first press with nothing changed; a second press closes the job with no proposal
export const NOTHING_CHANGED_TEXT = `Nothing has changed yet. If you're not going to change anything, press ${CHANGE_AMOUNTS_BUTTON} again.`;

// The Change amounts card while other new amounts wait for signatures (propose-change refuses a second)
export const AMOUNTS_ALREADY_OPEN_TEXT =
  "You can't change the amounts now. Other new amounts are waiting for the signatories to sign them. Once that change " +
  'is signed and complete, you can change the amounts again. You can cancel this job.';

// The Change card card's line about the card automatic payments use until the change is signed
export function oldCardLine(card: string): string {
  return `Until the change is signed, automatic payments keep using the ${card}.`;
}

/**
 * The line once a new card is held for the signatories' change (Change the automatic payments card, and Change card on
 * Automatic payment failed). `card` is "Visa •••• 4242" (cardDots), null when the card's details weren't answered.
 */
export function cardSavedText(card: string | null): string {
  // "Your card is saved." when the card's details are unknown: approved (round 4 follow-ups, small string 7)
  const saved = card ? `Your ${card} is saved.` : 'Your card is saved.';
  return `${saved} Once the signatories sign the change on the public ledger, it becomes the company's automatic payment card and payments resume.`;
}

// The signatories signed the change of card, but switching to the new card failed (5 tries), so it was freed. Built from
// the approved switch-failed notice (copy walkthrough c: its title and last two sentences); the notice itself says why
// (approved as built, round 4 follow-ups)
export const CARD_APPLY_FAILED_TEXT =
  'Changing the automatic payment card failed. The old card is still the automatic payment card. Please change the card again.';

// The reason a change button is disabled during a signatory change: autopaySetupAccess's, reused
export const SIGNATORY_CHANGE_REASON = 'Please wait: the signatory list is being changed';

// #40: why Change amounts and Change card are disabled while a stop waits for signatures (start-change refuses them then).
// Wording not yet reviewed
export const STOPPING_REASON = 'Automatic payments are being stopped';

type Metadata = { key: string; value: string | null }[] | Record<string, unknown> | null | undefined;

// A metadata value, from the engine's list of {key, value} or a plain object; null when absent or blank. Of a key listed
// more than once the last entry is read, as the engine reads it
export function metadataText(metadata: Metadata, key: string): string | null {
  const value = Array.isArray(metadata)
    ? [...metadata].reverse().find((m) => m?.key === key)?.value
    : (metadata as Record<string, unknown> | null | undefined)?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/**
 * The child workflow a card's `workflow/create` node started (or adopted): the engine writes its id to the parent's
 * metadata as `CREATED_WORKFLOW_<node id>`. Null until it has. A card that reopens (Raise operating target) runs the node
 * again and the engine adds another entry: the last is the current child.
 */
export function childWorkflowOf(parentMetadata: Metadata, nodeId: string): string | null {
  return metadataText(parentMetadata, `CREATED_WORKFLOW_${nodeId}`);
}

// The node of the card's workflow that starts each action's child (Limit reached and Change target 1.1.0, Raise operating
// target 1.0.0)
export const CHILD_NODE: Record<'raise' | 'change', 'change'> = {
  raise: 'change',
  change: 'change',
};

/**
 * The line a closed change card shows (W5 (4), round 3 extras), from the change workflow: amounts `changed`, card
 * `switched`; `not-changed`, `not-switched`, `cancelled` or a CANCELLED workflow say it was cancelled; a card change whose
 * switch to the new card failed (`apply-failed`) says so. While the workflow is still PENDING after its card was done
 * (the new amounts proposed, or round 15's card change recorded: TRANSACTION_ID), it says the change
 * waits for the signatories. Null when the workflow isn't read, or is still ending some other way: the card keeps its own
 * line then.
 */
export function changeDoneText(
  kind: 'AMOUNTS' | 'CARD',
  workflow: { status?: string | null; outcome?: string | null; metadata?: Metadata } | null | undefined,
): string | null {
  if (!workflow) return null;
  const outcome = workflow.outcome ?? null;
  if (kind === 'AMOUNTS' && outcome === 'changed') return CHANGE_DONE_TEXT.AMOUNTS;
  if (kind === 'CARD' && outcome === 'switched') return CHANGE_DONE_TEXT.CARD;
  if (kind === 'CARD' && outcome === 'apply-failed') return CARD_APPLY_FAILED_TEXT;
  const cancelled = kind === 'AMOUNTS' ? ['not-changed', 'cancelled'] : ['not-switched', 'cancelled'];
  if ((outcome && cancelled.includes(outcome)) || workflow.status === 'CANCELLED') return CHANGE_DONE_TEXT.CANCELLED;
  const sent = metadataText(workflow.metadata, 'TRANSACTION_ID');
  if (workflow.status === 'PENDING' && !outcome && sent) return CHANGE_SIGNING_TEXT[kind];
  return null;
}

// --- My Account ---

export type AutopayCardLabel = 'Change card' | 'Change automatic payment card';

export interface AutopayChangeAccess {
  visible: boolean;
  // Why Change amounts is disabled, or null
  amountsReason: string | null;
  // Why the card button is disabled, or null
  cardReason: string | null;
  // #40: why Stop automatic payments is disabled, or null. Another change waiting for signatures is said when it is
  // pressed (start-change answers blocked)
  stopReason: string | null;
  // The card button: "Change card" for the card's owner (`cardOwnerId`), "Change automatic payment card" for every other
  // signatory. Both start the same change of card
  cardLabel: AutopayCardLabel;
}

/**
 * The My Account Change amounts, card and (#40) Stop automatic payments buttons: for named signatories, once automatic
 * payments are on. A pending signatory change disables all three (start-change refuses then). A stop waiting for
 * signatures (`stopping`: pendingChange kind STOP) disables the two changes, which start-change refuses then; the stop
 * button itself opens the stop already waiting. A change already waiting for signatures is said when the button is
 * pressed (start-change answers waiting, or blocked for a stop).
 */
export function autopayChangeAccess(input: {
  isSignatory: boolean;
  setupState: string | null | undefined;
  changePending: boolean;
  isCardOwner: boolean;
  stopping?: boolean;
}): AutopayChangeAccess {
  const cardLabel: AutopayCardLabel = input.isCardOwner ? 'Change card' : 'Change automatic payment card';
  if (!input.isSignatory || input.setupState !== 'ACTIVE') {
    return { visible: false, amountsReason: null, cardReason: null, stopReason: null, cardLabel };
  }
  if (input.changePending) {
    return {
      visible: true,
      amountsReason: SIGNATORY_CHANGE_REASON,
      cardReason: SIGNATORY_CHANGE_REASON,
      stopReason: SIGNATORY_CHANGE_REASON,
      cardLabel,
    };
  }
  if (input.stopping) {
    return { visible: true, amountsReason: STOPPING_REASON, cardReason: STOPPING_REASON, stopReason: null, cardLabel };
  }
  return { visible: true, amountsReason: null, cardReason: null, stopReason: null, cardLabel };
}

// --- Change automatic payments amounts ---

export type ChangeSource = 'ACCOUNT' | 'LIMIT_REACHED' | 'CHANGE_TARGET' | 'RAISE_TARGET';
export type ChangeFocus = 'LIMIT' | 'TARGET' | 'OPERATING_TARGET';
// Which monthly limit a Limit reached card was raised for
export type ReachedLimit = 'CARD' | 'OPERATING';

const SOURCES: readonly ChangeSource[] = ['LIMIT_REACHED', 'CHANGE_TARGET', 'RAISE_TARGET'];
const FOCUSES: readonly ChangeFocus[] = ['LIMIT', 'TARGET', 'OPERATING_TARGET'];

/**
 * Where the change was started, what brought the signatory to it, (from a Limit reached card) which limit was reached and
 * (from a Raise operating target card) the operating target that covers what was needed, in whole credits.
 */
export function changeAmountsContext(metadata: Metadata): {
  source: ChangeSource;
  focus: ChangeFocus | null;
  limit: ReachedLimit | null;
  suggestedOperatingTargetCredits: number | null;
} {
  const source = metadataText(metadata, 'SOURCE');
  const focus = metadataText(metadata, 'FOCUS');
  const limit = metadataText(metadata, 'LIMIT');
  const suggested = metadataText(metadata, 'SUGGESTED_OPERATING_TARGET_CREDITS') ?? '';
  const suggestedCredits = /^\d+$/.test(suggested) ? Number(suggested) : null;
  return {
    source: SOURCES.includes(source as ChangeSource) ? (source as ChangeSource) : 'ACCOUNT',
    focus: FOCUSES.includes(focus as ChangeFocus) ? (focus as ChangeFocus) : null,
    limit: limit === 'CARD' || limit === 'OPERATING' ? limit : null,
    suggestedOperatingTargetCredits:
      suggestedCredits !== null && Number.isSafeInteger(suggestedCredits) && suggestedCredits > 0
        ? suggestedCredits
        : null,
  };
}

// The fields a change started from a card is about, first to be focused: the limit that was reached first
export function focusFields(focus: ChangeFocus | null, limit: ReachedLimit | null = null): AmountField[] {
  if (focus === 'LIMIT') {
    return limit === 'OPERATING'
      ? ['monthlyTopUpLimitPence', 'monthlyCardLimitPence']
      : ['monthlyCardLimitPence', 'monthlyTopUpLimitPence'];
  }
  if (focus === 'TARGET') return ['companyTargetPence', 'targetAlertPercent'];
  // The operating target can't be more than the company target buys (validateSettings), so the £ target comes next
  if (focus === 'OPERATING_TARGET') return ['operatingTargetCredits', 'companyTargetPence'];
  return [];
}

/**
 * The form's starting text for a change started from a Raise operating target card: the live amounts with the operating
 * target raised to the suggested one, never lowered. The form's own checks then say when the £ target must rise too.
 */
export function raiseTargetDraft(draft: SettingsDraft, suggestedCredits: number | null): SettingsDraft {
  if (suggestedCredits === null || !Number.isSafeInteger(suggestedCredits) || suggestedCredits <= 0) return draft;
  const live = /^\d+$/.test(draft.operatingTargetCredits) ? Number(draft.operatingTargetCredits) : null;
  if (live !== null && live >= suggestedCredits) return draft;
  return { ...draft, operatingTargetCredits: String(suggestedCredits) };
}

export type ChangeAmountsStage = 'DONE' | 'CLAIM' | 'LOADING' | 'PROPOSED' | 'CHANGE_OPEN' | 'LOCKED' | 'FORM';

/**
 * Where the Change automatic payments amounts card is (a company without automatic payments never gets one: start-change
 * refuses):
 * - DONE: the job is finished;
 * - CLAIM: nobody holds it (it was released);
 * - LOADING: the automatic payments row hasn't been read yet (nothing to show or press);
 * - PROPOSED: this card's proposal is recorded; the server completes the job a moment later;
 * - CHANGE_OPEN: other new amounts are waiting for signatures (propose-change refuses a second);
 * - LOCKED: a signatory change is pending (propose-change refuses then);
 * - FORM: the holder chooses the new amounts.
 */
export function changeAmountsStage(input: {
  completed: boolean;
  claimed: boolean;
  row: AutoPaymentRow | null | undefined;
  personId: string | null | undefined;
  proposedTransactionId: string | null;
  changePending: boolean;
  // false until the row has been read (or couldn't be)
  rowLoaded?: boolean;
}): ChangeAmountsStage {
  if (input.completed) return 'DONE';
  if (!input.claimed) return 'CLAIM';
  if (input.proposedTransactionId) return 'PROPOSED';
  if (input.rowLoaded === false) return 'LOADING';
  const pending = input.row?.pendingChange?.transactionId ?? null;
  if (pending) {
    const change = input.row?.pendingChange;
    const mine = change?.kind === 'AMOUNTS' && !!input.personId && change.requestedBy === input.personId;
    return mine ? 'PROPOSED' : 'CHANGE_OPEN';
  }
  if (input.changePending) return 'LOCKED';
  return 'FORM';
}

// "250" for 25000 pence, "250.50" for 25050: a pound amount as the form shows it
export function poundsText(pence: number | null | undefined): string {
  if (typeof pence !== 'number' || !Number.isSafeInteger(pence) || pence < 0) return '';
  const pounds = Math.floor(pence / 100);
  const rest = pence % 100;
  return rest === 0 ? String(pounds) : `${pounds}.${String(rest).padStart(2, '0')}`;
}

/** The form's starting text: the company's live amounts, as the signatories signed them. */
export function draftFromRow(row: Partial<AutoAmounts> | null | undefined): SettingsDraft {
  const whole = (value: unknown) => (typeof value === 'number' && Number.isSafeInteger(value) ? String(value) : '');
  const alert = row?.targetAlertPercent;
  return {
    companyThresholdCredits: whole(row?.companyThresholdCredits),
    companyTarget: poundsText(row?.companyTargetPence),
    operatingThresholdCredits: whole(row?.operatingThresholdCredits),
    operatingTargetCredits: whole(row?.operatingTargetCredits),
    monthlyCardLimit: poundsText(row?.monthlyCardLimitPence),
    monthlyTopUpLimit: poundsText(row?.monthlyTopUpLimitPence),
    alertOn: !row ? null : typeof alert === 'number',
    alertPercent: typeof alert === 'number' ? String(alert) : '',
  };
}

/** Whether new amounts are the live ones exactly: nothing would change, so there is nothing to sign. */
export function sameAmounts(amounts: AutoAmounts, live: Partial<AutoAmounts> | null | undefined): boolean {
  if (!live) return false;
  return (Object.keys(amounts) as (keyof AutoAmounts)[]).every((key) => (amounts[key] ?? null) === (live[key] ?? null));
}

export interface NowAfterRow {
  label: string;
  now: string;
  after: string;
  changed: boolean;
}

/**
 * The change-amounts signing card's Now / After table (G14): the live settings beside the proposed ones, a row each for the
 * company account's top-up threshold (credits, £), the card charge each time (£, credits), the monthly card limit, the
 * operating account's top-up threshold and target (credits), the monthly top-up limit and the price alert. Every
 * conversion is at the price the change was proposed at, as the footnote says. `changed` marks a row whose values differ.
 * Null when the proposal is missing an amount.
 */
export function nowAfterRows(
  live: AutoPaymentRow | null | undefined,
  proposed: ProposedSettings | null | undefined,
): NowAfterRow[] | null {
  if (!proposed) return null;
  const isWhole = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value);
  const required = [
    proposed.companyThresholdCredits,
    proposed.companyTargetPence,
    proposed.operatingThresholdCredits,
    proposed.operatingTargetCredits,
    proposed.monthlyCardLimitPence,
    proposed.monthlyTopUpLimitPence,
  ];
  if (!required.every(isWhole)) return null;
  const unit =
    typeof proposed.unitPricePence === 'number' && proposed.unitPricePence > 0 ? proposed.unitPricePence : null;
  const credits = (value: number | null | undefined) => {
    if (!isWhole(value)) return 'Not set';
    return `${formatCredits(value)} credits${unit ? ` (about ${formatPence(penceForCredits(value, unit))})` : ''}`;
  };
  const pounds = (value: number | null | undefined, buys: boolean) => {
    if (!isWhole(value)) return 'Not set';
    return `${formatPence(value)}${buys && unit ? ` (buys ${formatCredits(creditsForPence(value, unit))} credits)` : ''}`;
  };
  const operating = (threshold: number | null | undefined, target: number | null | undefined) =>
    isWhole(threshold) && isWhole(target)
      ? `${formatCredits(threshold)} / ${formatCredits(target)} credits`
      : 'Not set';
  const alert = (percent: number | null | undefined, set: boolean) => {
    if (!set) return 'Not set';
    return typeof percent === 'number' ? `Below ${percent}% of the credits at signing` : 'Off';
  };
  const row = (
    label: string,
    now: string,
    after: string,
    nowValues: unknown[],
    afterValues: unknown[],
  ): NowAfterRow => ({
    label,
    now,
    after,
    changed: nowValues.some((value, i) => (value ?? null) !== (afterValues[i] ?? null)),
  });
  const l = live ?? {};
  const liveSet = !!live && live.setupState === 'ACTIVE';
  return [
    row(
      'Company account top-up below',
      credits(l.companyThresholdCredits),
      credits(proposed.companyThresholdCredits),
      [l.companyThresholdCredits],
      [proposed.companyThresholdCredits],
    ),
    row(
      'Card charge each time',
      pounds(l.companyTargetPence, true),
      pounds(proposed.companyTargetPence, true),
      [l.companyTargetPence],
      [proposed.companyTargetPence],
    ),
    row(
      'Monthly card limit',
      pounds(l.monthlyCardLimitPence, false),
      pounds(proposed.monthlyCardLimitPence, false),
      [l.monthlyCardLimitPence],
      [proposed.monthlyCardLimitPence],
    ),
    row(
      'Operating top-up below / up to',
      operating(l.operatingThresholdCredits, l.operatingTargetCredits),
      operating(proposed.operatingThresholdCredits, proposed.operatingTargetCredits),
      [l.operatingThresholdCredits, l.operatingTargetCredits],
      [proposed.operatingThresholdCredits, proposed.operatingTargetCredits],
    ),
    row(
      'Monthly top-up limit',
      pounds(l.monthlyTopUpLimitPence, false),
      pounds(proposed.monthlyTopUpLimitPence, false),
      [l.monthlyTopUpLimitPence],
      [proposed.monthlyTopUpLimitPence],
    ),
    row(
      'Price alert',
      alert(l.targetAlertPercent, liveSet),
      alert(proposed.targetAlertPercent, true),
      [liveSet ? (l.targetAlertPercent ?? null) : undefined],
      [proposed.targetAlertPercent ?? null],
    ),
  ];
}

// The Now / After table's footnote: the price the conversions use
export function nowAfterRateNote(proposed: ProposedSettings | null | undefined): string | null {
  const unit = proposed?.unitPricePence;
  if (typeof unit !== 'number' || !(unit > 0)) return null;
  return `Price per credit when proposed: ${formatUnitPence(unit)}.`;
}

// --- Change the automatic payments card ---

export type ChangeCardStage = 'DONE' | 'CLAIM' | 'LOADING' | 'LOCKED' | 'CARD';

/**
 * Where the Change the automatic payments card card is (a company without automatic payments never gets one: start-change
 * refuses):
 * - DONE: the job is finished (the card is held and its record made, or the change ended);
 * - CLAIM: nobody holds it;
 * - LOADING: the automatic payments row hasn't been read yet (nothing to show or press);
 * - LOCKED: a signatory change is pending (the record is signed under the account rule, which is changing);
 * - CARD: the holder enters the new card.
 */
export function changeCardStage(input: {
  completed: boolean;
  claimed: boolean;
  changePending: boolean;
  // false until the row has been read (or couldn't be)
  rowLoaded?: boolean;
}): ChangeCardStage {
  if (input.completed) return 'DONE';
  if (!input.claimed) return 'CLAIM';
  if (input.rowLoaded === false) return 'LOADING';
  if (input.changePending) return 'LOCKED';
  return 'CARD';
}
